// Redactor's own cleaner (cleaner.input) strips a short list of on* handlers from a
// short list of tags: onanimationend is not in it, <input> is not in it, and deniedTags
// omits <style>. So HTML typed in the editor's source mode re-enters the contenteditable
// as live markup. This runs on the paths that feed the editor and the textarea. It only
// stops execution in the author's own browser — the server-side allowlist stays the
// authority on what gets stored and displayed.

const DANGEROUS_ELEMENTS = [
  "applet",
  "base",
  "button",
  "embed",
  "form",
  "frame",
  "frameset",
  "input",
  "link",
  "math",
  "meta",
  "noscript",
  "object",
  "script",
  "select",
  "style",
  "svg",
  "textarea",
].join(",");

const URL_ATTRIBUTES = ["href", "src", "action", "formaction"];
const UNSAFE_PROTOCOL = /^\s*(javascript|vbscript|data):/i;

// The video and googleMaps buttons insert an iframe, so iframes cannot be denied
// outright. Mirror of RedactorScrubber::EMBED_SRC_PATTERN, IFRAME_ALLOWED_ATTRIBUTES
// and the sandbox it forces — keep both sides in sync or the editor and the stored
// value drift apart.
const EMBED_SRC = new RegExp(
  [
    "^(?:https?:)?//(?:www\\.)?youtube(?:-nocookie)?\\.com/",
    "^(?:https?:)?//(?:www\\.|player\\.)?vimeo\\.com/",
    "^https?://(?:www\\.google\\.com/maps|maps\\.google\\.com/maps)/",
  ].join("|"),
  "i"
);
const IFRAME_ALLOWED_ATTRIBUTES = [
  "allow",
  "allowfullscreen",
  "frameborder",
  "height",
  "loading",
  "sandbox",
  "src",
  "style",
  "title",
  "width",
];
const IFRAME_SANDBOX = "allow-scripts allow-same-origin allow-popups";

function scrubIframe(iframe) {
  if (!EMBED_SRC.test(iframe.getAttribute("src") || "")) {
    iframe.remove();
    return;
  }

  [...iframe.attributes].forEach((attribute) => {
    if (!IFRAME_ALLOWED_ATTRIBUTES.includes(attribute.name.toLowerCase())) {
      iframe.removeAttribute(attribute.name);
    }
  });

  iframe.setAttribute("sandbox", IFRAME_SANDBOX);
}

export function scrubHtml(html) {
  if (typeof html !== "string" || html === "") return html;

  // createHTMLDocument has no browsing context, so parsing here runs no script,
  // fires no handler and fetches no resource. Never parse into the live document.
  const doc = document.implementation.createHTMLDocument("");
  doc.body.innerHTML = html;

  doc.body.querySelectorAll(DANGEROUS_ELEMENTS).forEach((node) => node.remove());
  doc.body.querySelectorAll("iframe").forEach(scrubIframe);

  doc.body.querySelectorAll("*").forEach((element) => {
    [...element.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();

      if (name.startsWith("on")) element.removeAttribute(attribute.name);

      if (URL_ATTRIBUTES.includes(name) && UNSAFE_PROTOCOL.test(attribute.value)) {
        element.removeAttribute(attribute.name);
      }
    });

    if (element.tagName === "A" && (element.getAttribute("target") || "").toLowerCase() === "_blank") {
      element.setAttribute("rel", "noopener noreferrer");
    }
  });

  return doc.body.innerHTML;
}

// source.open matters on its own: close() scrubs into the editor but leaves the form
// textarea alone, and Redactor's sync only rewrites it when the editor content changed —
// so on an empty field the text typed in source mode survives untouched and open() reads
// it straight back.
export const redactorScrubCallbacks = {
  pasteBefore: scrubHtml,
  "source.open": scrubHtml,
  "source.close": scrubHtml,
  syncing: scrubHtml,
};

// Same reason: without this the browser posts whatever source mode left in the textarea.
// The server scrubs it anyway, the point is not to send it at all.
document.addEventListener(
  "submit",
  (event) => {
    const form = event.target;
    if (!form || typeof form.querySelectorAll !== "function") return;

    form.querySelectorAll("textarea.redactor, textarea.redactor-banner").forEach((textarea) => {
      textarea.value = scrubHtml(textarea.value);
    });
  },
  true
);
